/**
 * Variant Picker Core (Base Class)
 * =================================
 * Abstract base class providing shared functionality for variant picker implementations.
 * Extend this class to create specialized variant pickers (single-option, multi-option, etc.)
 *
 * @abstract
 * @class VariantPickerBase
 *
 * Features:
 * - Search functionality with no-results handling
 * - Button selection state management (aria-pressed, classes)
 * - Add to cart button enable/disable
 * - URL variant parameter management
 * - Event dispatching for variant changes
 * - Form validation
 * - Reset functionality
 * - Auto-initialization pattern
 */

class VariantPickerBase {
  /**
   * Creates a new VariantPickerBase instance
   * @param {HTMLElement} element - The container element for the variant picker
   */
  constructor(element) {
    if (new.target === VariantPickerBase) {
      throw new Error('VariantPickerBase is abstract and cannot be instantiated directly');
    }

    this.container = element;
    this.productId = this.container.dataset.productId || null;
    this.totalVariants = parseInt(this.container.dataset.totalVariants) || 0;

    // Common element references - child classes may add more
    this.variantIdInput = this.container.querySelector('[data-variant-id-input]');
    this.searchInputs = this.container.querySelectorAll('[data-search-input]');

    // State
    this.selectedVariantId = null;
    this.currentVariant = null;
    this.isInitializing = true;

    // Product section reference (for updating price, SKU, etc.)
    this.productSection = document.querySelector('.js-product');
  }

  /**
   * Initialize the variant picker - must be called by child class after setup
   * @protected
   */
  _baseInit() {
    this.setupFormValidation();
    this.dispatchReadyEvent();
    this.isInitializing = false;
  }

  // ============================================================
  // SEARCH FUNCTIONALITY
  // ============================================================

  /**
   * Handles search input filtering of variant buttons
   * @param {Event} event - The input event
   * @param {NodeList|HTMLElement[]} buttons - The buttons to filter
   * @param {HTMLElement|null} noResultsEl - Element to show when no results
   * @protected
   */
  _handleSearch(event, buttons, noResultsEl = null) {
    const searchTerm = event.target.value.toLowerCase().trim();
    let hasVisibleResults = false;

    buttons.forEach(button => {
      // Support multiple data attributes for search matching
      const value = (button.dataset.variantValue || button.dataset.optionValue || '').toLowerCase();
      const originalValue = (button.dataset.originalValue || '').toLowerCase();
      const displayValue = (button.dataset.displayValue || '').toLowerCase();

      // Search in all value fields
      const isMatch = value.includes(searchTerm) ||
                      originalValue.includes(searchTerm) ||
                      displayValue.includes(searchTerm);

      // Show/hide button
      button.style.display = isMatch ? '' : 'none';
      if (isMatch && !button.disabled) {
        hasVisibleResults = true;
      }
    });

    // Show/hide "no results" message
    if (noResultsEl) {
      noResultsEl.hidden = hasVisibleResults || searchTerm.length === 0;

      // Update search term display if present
      const searchTermDisplay = noResultsEl.querySelector('[data-search-term]');
      if (searchTermDisplay) {
        searchTermDisplay.textContent = searchTerm;
      }
    }

    return hasVisibleResults;
  }

  /**
   * Clears all search inputs and resets visibility
   * @protected
   */
  _clearSearchInputs() {
    this.searchInputs.forEach(input => {
      input.value = '';
      const event = new Event('input', { bubbles: true });
      input.dispatchEvent(event);
    });
  }

  // ============================================================
  // BUTTON SELECTION STATE
  // ============================================================

  /**
   * Updates button selection states within a group
   * @param {HTMLElement} selectedButton - The newly selected button
   * @param {NodeList|HTMLElement[]} allButtons - All buttons in the group
   * @protected
   */
  _updateButtonStates(selectedButton, allButtons) {
    // Deselect all buttons
    allButtons.forEach(btn => {
      btn.setAttribute('aria-pressed', 'false');
      btn.classList.remove('is-selected');
    });

    // Select the target button
    selectedButton.setAttribute('aria-pressed', 'true');
    selectedButton.classList.add('is-selected');
  }

  /**
   * Resets all button states in the container
   * @param {string} buttonSelector - CSS selector for buttons
   * @protected
   */
  _resetAllButtonStates(buttonSelector = '[data-variant-button], [data-option-button]') {
    this.container.querySelectorAll(buttonSelector).forEach(btn => {
      btn.setAttribute('aria-pressed', 'false');
      btn.classList.remove('is-selected');
    });
  }

  /**
   * Scrolls a button into view on mobile devices
   * @param {HTMLElement} button - The button to scroll into view
   * @param {number} breakpoint - The mobile breakpoint width (default: 640)
   * @protected
   */
  _scrollButtonIntoView(button, breakpoint = 640) {
    if (window.innerWidth <= breakpoint && !this.isInitializing) {
      button.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }

  // ============================================================
  // HIDDEN INPUT MANAGEMENT
  // ============================================================

  /**
   * Updates the hidden variant ID input
   * @param {string|number|null} variantId - The variant ID to set
   * @protected
   */
  _updateVariantIdInput(variantId) {
    if (this.variantIdInput) {
      this.variantIdInput.value = variantId || '';

      // Dispatch change event for form listeners
      const changeEvent = new Event('change', { bubbles: true });
      this.variantIdInput.dispatchEvent(changeEvent);
    }
  }

  // ============================================================
  // ADD TO CART BUTTON MANAGEMENT
  // ============================================================

  /**
   * Gets the add to cart button element
   * @returns {HTMLElement|null}
   * @protected
   */
  _getAddToCartButton() {
    return document.querySelector('[data-add-to-cart-text], [name="add"]');
  }

  /**
   * Enables the add to cart button with default text
   * @param {string|null} customText - Optional custom button text
   * @protected
   */
  _enableAddToCartButton(customText = null) {
    const addToCartButton = this._getAddToCartButton();
    if (!addToCartButton) return;

    addToCartButton.disabled = false;
    addToCartButton.style.display = '';
    addToCartButton.classList.remove('btn--needs-selection');

    const defaultText = addToCartButton.dataset.addToCartText || 'Add to Cart';
    addToCartButton.textContent = customText || defaultText;
  }

  /**
   * Disables the add to cart button
   * @param {string|null} customText - Optional custom button text (e.g., "Select a size first")
   * @protected
   */
  _disableAddToCartButton(customText = null) {
    const addToCartButton = this._getAddToCartButton();
    if (!addToCartButton) return;

    addToCartButton.disabled = true;
    addToCartButton.classList.add('btn--needs-selection');

    if (customText) {
      addToCartButton.textContent = customText;
    }
  }

  /**
   * Hides the add to cart button completely
   * @protected
   */
  _hideAddToCartButton() {
    const addToCartButton = this._getAddToCartButton();
    const buyButtons = document.querySelector('.product-info__buy-buttons');

    if (addToCartButton) {
      addToCartButton.style.display = 'none';
    }
    if (buyButtons) {
      buyButtons.style.display = 'none';
    }
  }

  /**
   * Shows the add to cart button
   * @protected
   */
  _showAddToCartButton() {
    const addToCartButton = this._getAddToCartButton();
    const buyButtons = document.querySelector('.product-info__buy-buttons');

    if (addToCartButton) {
      addToCartButton.style.display = '';
    }
    if (buyButtons) {
      buyButtons.style.display = '';
    }
  }

  // ============================================================
  // URL MANAGEMENT
  // ============================================================

  /**
   * Updates the URL with the selected variant ID
   * @param {string|number} variantId - The variant ID to add to URL
   * @protected
   */
  _updateURL(variantId) {
    if (window.history.replaceState) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    }
  }

  /**
   * Gets the variant ID from the URL
   * @returns {string|null} The variant ID or null
   * @protected
   */
  _getURLVariantId() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('variant');
  }

  /**
   * Removes the variant parameter from URL
   * @protected
   */
  _clearURLVariant() {
    if (window.history.replaceState) {
      const url = new URL(window.location);
      url.searchParams.delete('variant');
      window.history.replaceState({}, '', url);
    }
  }

  // ============================================================
  // EVENT DISPATCHING
  // ============================================================

  /**
   * Dispatches the picker ready event
   * @protected
   */
  dispatchReadyEvent() {
    this.container.dispatchEvent(new CustomEvent('variant-picker:ready', {
      bubbles: true,
      detail: {
        productId: this.productId,
        totalVariants: this.totalVariants,
        pickerType: this.constructor.name
      }
    }));
  }

  /**
   * Dispatches variant selected event - can be overridden by child classes
   * @param {string|number} variantId - The selected variant ID
   * @param {Object} additionalDetail - Additional data to include in event detail
   * @protected
   */
  _dispatchVariantSelectedEvent(variantId, additionalDetail = {}) {
    const eventDetail = {
      variantId: variantId,
      productId: this.productId,
      ...additionalDetail
    };

    // Primary event on container (bubbles to document)
    this.container.dispatchEvent(new CustomEvent('variant:selected', {
      bubbles: true,
      detail: eventDetail
    }));

    // Global document event for legacy compatibility
    document.dispatchEvent(new CustomEvent('variant:changed', {
      detail: eventDetail
    }));
  }

  /**
   * Dispatches theme-compatible variant change event
   * Used for integration with Shopify theme price/image/SKU updates
   * @param {Object} variant - The variant object with id, price, sku, etc.
   * @protected
   */
  _dispatchThemeVariantChange(variant) {
    const section = this.productSection || document.querySelector('.js-product');
    const productForm = section?.querySelector('.js-product-form-main');

    if (section) {
      section.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: productForm,
          variant: variant,
          product: { id: this.productId }
        }
      }));
    }
  }

  /**
   * Dispatches variant ready event (for addons like frame-addon)
   * @param {Object|null} variant - The variant object or null
   * @param {boolean} allOptionsSelected - Whether all required options are selected
   * @protected
   */
  _dispatchVariantReadyEvent(variant, allOptionsSelected) {
    this.container.dispatchEvent(new CustomEvent('variant:ready', {
      bubbles: true,
      detail: { variant, allOptionsSelected }
    }));
  }

  // ============================================================
  // FORM VALIDATION
  // ============================================================

  /**
   * Sets up form validation to prevent submission without variant selection
   * @protected
   */
  setupFormValidation() {
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (!productForm || !this.variantIdInput) return;

    productForm.addEventListener('submit', (e) => {
      if (!this._validateSelection()) {
        e.preventDefault();
        this._handleValidationError();
      }
    });
  }

  /**
   * Validates that a variant is selected - override in child classes
   * @returns {boolean}
   * @protected
   */
  _validateSelection() {
    return !!this.selectedVariantId;
  }

  /**
   * Handles validation error - override in child classes for custom behavior
   * @protected
   */
  _handleValidationError() {
    alert('Please select a variant first');
    this._focusFirstInput();
  }

  /**
   * Focuses the first input or button for accessibility
   * @protected
   */
  _focusFirstInput() {
    const searchInput = this.container.querySelector('[data-search-input]');
    if (searchInput) {
      searchInput.focus();
      return;
    }

    const firstButton = this.container.querySelector('[data-variant-button], [data-option-button]');
    if (firstButton) {
      firstButton.focus();
    }
  }

  // ============================================================
  // PRICE FORMATTING
  // ============================================================

  /**
   * Formats a price in cents to currency string
   * @param {number} cents - The price in cents
   * @param {string} currency - The currency code (default: USD)
   * @param {string} locale - The locale (default: en-US)
   * @returns {string} Formatted price string
   * @protected
   */
  _formatPrice(cents, currency = 'USD', locale = 'en-US') {
    const dollars = cents / 100;
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(dollars);
  }

  // ============================================================
  // PUBLIC API - Override in child classes
  // ============================================================

  /**
   * Selects a variant by ID - must be implemented by child classes
   * @param {string|number} variantId - The variant ID to select
   * @returns {boolean} Whether the selection was successful
   * @abstract
   */
  selectVariant(variantId) {
    throw new Error('selectVariant must be implemented by child class');
  }

  /**
   * Gets the currently selected variant - must be implemented by child classes
   * @returns {Object} Object containing variant information
   * @abstract
   */
  getSelectedVariant() {
    throw new Error('getSelectedVariant must be implemented by child class');
  }

  /**
   * Resets the picker to initial state - must be implemented by child classes
   * @abstract
   */
  reset() {
    throw new Error('reset must be implemented by child class');
  }

  // ============================================================
  // STATIC INITIALIZATION HELPERS
  // ============================================================

  /**
   * Creates an auto-initialization function for a picker class
   * @param {string} selector - CSS selector for picker containers
   * @param {Function} PickerClass - The picker class constructor
   * @param {string} instanceProperty - Property name to store instance on element
   * @param {string} globalProperty - Window property name for instances array
   * @returns {Function} Initialization function
   * @static
   */
  static createInitializer(selector, PickerClass, instanceProperty, globalProperty) {
    return function() {
      const pickers = document.querySelectorAll(selector);
      const instances = [];

      pickers.forEach(picker => {
        const instance = new PickerClass(picker);
        instances.push(instance);

        // Store instance on element for external access
        picker[instanceProperty] = instance;
      });

      // Store all instances globally for debugging
      window[globalProperty] = instances;

      return instances;
    };
  }

  /**
   * Sets up DOM ready initialization
   * @param {Function} initFunction - The initialization function to call
   * @static
   */
  static initOnReady(initFunction) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initFunction);
    } else {
      initFunction();
    }
  }
}

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.VariantPickerBase = VariantPickerBase;
}
